import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import { FileSpreadsheet, Package, AlertCircle } from 'lucide-react';
import { useState } from 'react';

import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import useExportExcel from '@/hooks/useExportExcel';
import { DataTable } from '@/UI/TableShadcn';
import { cn } from '@/lib/utils';

import type { IItemCheckerReport } from '../../api/ItemChecker.interface';
import { ItemFilter } from '../Filters';

import { columns } from './Columns';

import 'dayjs/locale/ar';

dayjs.extend(relativeTime);
dayjs.locale('ar');

type Props = {
  items: IItemCheckerReport[] | undefined;
};

function ItemTable({ items }: Props) {
  const [filteredData, setFilteredData] = useState<
    IItemCheckerReport[] | undefined
  >(items);

  const exportToExcel = useExportExcel(
    filteredData || [],
    'نواقص الأصناف.xlsx',
  );

  return (
    <div className="p-4">
      {filteredData && filteredData.length > 0 ? (
        <div className="space-y-4">
          {/* Enhanced Table Header */}
          <Card
            className={cn(
              'border-0 p-4',
              'bg-gradient-to-r from-red-50/50 via-orange-50/50 to-amber-50/50',
              'dark:from-red-950/20 dark:via-orange-950/20 dark:to-amber-950/20',
              'shadow-md',
            )}
          >
            <div className="flex items-center justify-between gap-4">
              <div className="flex items-center gap-3">
                <div className="rounded-lg bg-gradient-to-br from-red-600 to-orange-600 p-2 text-white shadow-lg">
                  <Package className="h-5 w-5" />
                </div>
                <div>
                  <p className="text-sm font-semibold text-foreground">
                    عرض {filteredData.length} صنف
                  </p>
                  <p className="text-xs text-muted-foreground">
                    جميع الأصناف التي تحتاج لمتابعة
                  </p>
                </div>
              </div>

              <div className="flex items-center gap-2">
                {items && (
                  <ItemFilter data={items} onFilterChange={setFilteredData} />
                )}

                {/* Enhanced Export Button */}
                <Button
                  size="sm"
                  onClick={exportToExcel}
                  className={cn(
                    'relative overflow-hidden transition-all duration-300',
                    'bg-gradient-to-r from-emerald-600 to-green-600',
                    'hover:from-emerald-700 hover:to-green-700',
                    'dark:from-emerald-600 dark:to-green-600',
                    'dark:hover:from-emerald-700 dark:hover:to-green-700',
                    'text-white shadow-lg hover:scale-105 hover:shadow-xl',
                    'border border-white/20',
                    'group',
                  )}
                >
                  <span className="relative z-10 flex items-center gap-2">
                    <FileSpreadsheet className="h-4 w-4 transition-transform group-hover:rotate-12" />
                    <span className="font-medium">تصدير Excel</span>
                    <Badge
                      variant="outline"
                      className="ml-1 h-5 border-white/30 bg-white/20 px-1 text-[10px] text-white"
                    >
                      {filteredData.length}
                    </Badge>
                  </span>
                  <div className="absolute inset-0 translate-x-[-100%] bg-gradient-to-r from-transparent via-white/20 to-transparent transition-transform duration-700 group-hover:translate-x-[100%]" />
                </Button>
              </div>
            </div>
          </Card>

          {/* Data Table */}
          <div className="overflow-hidden rounded-xl border border-border/50 bg-background/50 shadow-xl backdrop-blur-sm">
            <DataTable
              columns={columns}
              data={filteredData}
              isPaginated
              isTopToolbar
              isRowSelection
              visibleColumns={['Company_Name', 'Place_Name', 'Count_Other']}
              heightTable="65vh"
            />
          </div>
        </div>
      ) : (
        <div className="flex h-[65vh] flex-col items-center justify-center rounded-xl border border-border/30 bg-gradient-to-br from-muted/20 to-muted/10">
          <div className="flex flex-col items-center justify-center p-8">
            <div className="relative">
              <div className="absolute inset-0 bg-gradient-to-r from-red-500/20 to-orange-500/10 blur-3xl" />
              <div className="relative rounded-full bg-gradient-to-br from-red-100 to-orange-100 p-6 dark:from-red-950/30 dark:to-orange-950/30">
                <AlertCircle className="h-20 w-20 text-red-500 dark:text-red-400" />
              </div>
            </div>
            <div className="mt-6 space-y-2 text-center">
              <h3 className="text-2xl font-bold text-foreground">
                لا توجد أصناف للعرض
              </h3>
              <p className="text-sm text-muted-foreground">
                قم بالبحث عن الأصناف التي تحتاج لمتابعة
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default ItemTable;
